import React, { useState, useEffect, useRef } from 'react';
import { authApi } from '../services/api';
import { supabase, exchangeCodeIfPresent } from '../lib/supabase';
import TermsContent from '../components/TermsContent';

const TERMS_STORAGE_KEY = 'loconvo_terms_v1';
const TERMS_VERSION = 'v1';

const reserved = new Set(['admin', 'administrator', 'root', 'mod', 'moderator', 'support', 'help', 'api', 'null', 'undefined', 'convoloc', 'loconvo']);

function validateUsername(u: string): string | null {
  const name = u.trim();
  if (name.length < 3 || name.length > 20) return '3–20 characters';
  if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(name)) return 'Start with a letter; letters/numbers/_ only';
  if (/__/.test(name) || /_$/.test(name)) return 'No double or trailing underscore';
  if (/^\d+$/.test(name)) return 'Cannot be only digits';
  if (reserved.has(name.toLowerCase())) return 'Reserved username';
  return null;
}

const SetupUsernamePage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usernameStatus, setUsernameStatus] = useState<{ ok: boolean; msg?: string }>({ ok: false });
  const [checking, setChecking] = useState(false);
  const [userId, setUserId] = useState<string>('Loading...');
  const [envUrl, setEnvUrl] = useState<string>('');
  const [agree, setAgree] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return localStorage.getItem(TERMS_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [showTerms, setShowTerms] = useState(false);
  const [termsScrolled, setTermsScrolled] = useState(false);
  const termsRef = useRef<HTMLDivElement | null>(null);

  // Check if user is authenticated
  useEffect(() => {
    const checkAuth = async () => {
      await exchangeCodeIfPresent();
      if (!supabase) {
        setError('Authentication is not configured. Please contact support.');
        return;
      }

      setEnvUrl(process.env.REACT_APP_SUPABASE_URL || 'Not Set');

      const { data } = await supabase.auth.getSession();
      if (!data.session) {
        setError('We could not complete sign-in. Please try again.');
        setUserId('No Session');
        return;
      }
      setUserId(data.session.user.id);

      // Check if user already has a username
      try {
        const user = await authApi.getCurrentUser();
        if (user.username) {
          // User already has username, redirect to home
          window.location.href = '/';
          return;
        }
      } catch (err: any) {
        const status = err?.response?.status;
        const code = err?.response?.data?.code;
        if (status === 404 && (code === 'PROFILE_NOT_FOUND' || !code)) {
          return; // Allowed path for new social signups that haven't provisioned a profile yet
        }
        if (status === 503 && code === 'AUTH_PROVIDER_UNVERIFIED') {
          // Do not redirect to login immediately to avoid loops if the issue is persistent (e.g. config error)
          setError('System Error: Unable to verify account status with identity provider. Please contact support.');
          return;
        }
        if (status === 401 || status === 404 || status === 410 || code === 'SUPABASE_ACCOUNT_DELETED') {
          try { await supabase.auth.signOut(); } catch { /* ignore */ }
          // Do not redirect automatically to avoid loops. Show error instead.
          // window.location.href = '/login';
          const backendMsg = err?.response?.data?.error;
          setError(backendMsg || ('Account access issue: ' + (code || 'Session expired or account unavailable') + '. Please sign in again.'));
          return;
        }
        // User doesn't exist in backend yet, that's okay (only for new signups)
      }
    };

    checkAuth();
  }, []);

  // Live username validation
  useEffect(() => {
    let active = true;
    setChecking(true);

    const run = async () => {
      const err = validateUsername(username);
      if (err || !username.trim()) {
        setUsernameStatus({ ok: false, msg: err || 'Enter a username' });
        setChecking(false);
        return;
      }

      try {
        const r = await authApi.checkUsername(username.trim());
        if (!active) return;
        setUsernameStatus(r.available ? { ok: true } : { ok: false, msg: 'Username taken' });
      } catch {
        if (!active) return;
        setUsernameStatus({ ok: false, msg: 'Unable to check' });
      } finally {
        setChecking(false);
      }
    };

    const t = setTimeout(run, 350);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [username]);

  const handleTermsScroll = () => {
    const el = termsRef.current;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
    if (atBottom) setTermsScrolled(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!agree) {
      setShowTerms(true);
      setTermsScrolled(false);
      setError('Please read and accept the Terms to continue.');
      return;
    }

    const name = username.trim();
    const validationError = validateUsername(name);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (!usernameStatus.ok) {
      setError(usernameStatus.msg || 'Username unavailable');
      return;
    }

    setSubmitting(true);

    try {
      await authApi.setUsername(name);
      await authApi.acceptTerms(TERMS_VERSION);
      try { localStorage.setItem(TERMS_STORAGE_KEY, 'true'); } catch { /* ignore */ }
      // Redirect to home
      window.location.href = '/';
    } catch (err: any) {
      const status = err?.response?.status;
      const code = err?.response?.data?.code;

      // Handle deleted account - user was deleted from Supabase while they had a valid JWT
      if (status === 410 || code === 'SUPABASE_ACCOUNT_DELETED') {
        try { await supabase?.auth.signOut(); } catch { /* ignore */ }
        const backendMsg = err?.response?.data?.error;
        setError(backendMsg || 'Your account was deleted and cannot be recreated. Please contact support if this is an error.');
        return;
      }

      // Handle expired or invalid session
      if (status === 401) {
        try { await supabase?.auth.signOut(); } catch { /* ignore */ }
        const backendMsg = err?.response?.data?.error;
        setError(backendMsg || 'Your session expired. Please sign in again.');
        return;
      }

      // Handle other errors
      setError(err?.response?.data?.error || err?.message || 'Failed to set username');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <div className="auth-wrapper" style={{ fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif' }}>
      <div className="auth-card">
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <h1 className="auth-title">Choose Your Username</h1>
          <p className="auth-subtitle">Pick a public username. It cannot be changed later.</p>
        </div>

        {error && (
          <div style={{ background: '#FEE2E2', border: '1px solid #EF4444', color: '#991B1B', padding: '16px', borderRadius: 8, marginBottom: 24, fontSize: 14 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Unable to Proceed</div>
            <div style={{ marginBottom: 12 }}>{error}</div>

            {/* Debugging Info for the User */}
            <div style={{ marginTop: 12, padding: '8px', background: 'rgba(255,255,255,0.5)', borderRadius: 4, fontSize: 12, fontFamily: 'monospace' }}>
              <div><strong>Debug Info:</strong></div>
              <div>User ID: {userId}</div>
              <div>Frontend URL: {envUrl}</div>
              {error.includes('SUPABASE_ACCOUNT_DELETED') && (
                <div style={{ marginTop: 4, color: '#7f1d1d' }}>
                  <strong>Possible Causes:</strong>
                  <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
                    <li><strong>Stale Session:</strong> You are signed in as a user that was deleted. Sign out below.</li>
                    <li><strong>Env Mismatch:</strong> Backend is connecting to a different Supabase project than Frontend. Check <code>SUPABASE_URL</code> in backend <code>.env</code>.</li>
                  </ul>
                </div>
              )}
            </div>

            <div style={{ marginTop: 16 }}>
              <button
                type="button"
                onClick={async () => {
                  try { await supabase?.auth.signOut(); } catch { }
                  localStorage.clear(); // Force clear everything
                  window.location.href = '/login';
                }}
                style={{
                  background: '#991B1B',
                  border: 'none',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600
                }}
              >
                Sign Out & Start Over
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gap: 16, marginBottom: 24 }}>
            <div style={{ display: 'grid', gap: 8 }}>
              <label htmlFor="username" style={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>
                Username
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="e.g. biker_joe"
                disabled={submitting}
                autoFocus
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 16,
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = '#3b82f6')}
                onBlur={(e) => (e.currentTarget.style.borderColor = '#e5e7eb')}
              />
              {username && (
                <div style={{ fontSize: 12, marginTop: 2, color: checking ? '#6b7280' : usernameStatus.ok ? '#059669' : '#b91c1c' }}>
                  {checking ? 'Checking...' : usernameStatus.ok ? '✓ Available' : (usernameStatus.msg || 'Invalid')}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <input
                type="checkbox"
                checked={agree}
                readOnly
                onClick={() => { setShowTerms(true); setTermsScrolled(false); }}
              />
              <span style={{ color: '#374151', fontSize: 14 }}>I have read and agree to the Terms &amp; Acceptable Use</span>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => { setShowTerms(true); setTermsScrolled(false); }}
              >
                View
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting || !usernameStatus.ok || checking || !agree}
            className="btn btn-primary"
            style={{
              width: '100%',
              padding: '12px 16px',
              fontSize: 16,
              fontWeight: 600,
              boxSizing: 'border-box',
              opacity: (submitting || !usernameStatus.ok || checking || !agree) ? 0.5 : 1,
              cursor: (submitting || !usernameStatus.ok || checking || !agree) ? 'not-allowed' : 'pointer'
            }}
          >
            {submitting ? 'Setting username...' : 'Continue'}
          </button>
        </form>

        <div style={{ marginTop: 24, padding: '12px', background: '#F3F4F6', borderRadius: 8, fontSize: 13, color: '#6b7280' }}>
          <strong>Username requirements:</strong>
          <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
            <li>3-20 characters</li>
            <li>Start with a letter</li>
            <li>Letters, numbers, and underscores only</li>
            <li>Cannot be changed later</li>
          </ul>
        </div>
      </div>
    </div>
    {showTerms && (
      <div className="modal-backdrop" role="dialog" aria-modal="true">
        <div className="modal-card" style={{ maxWidth: 680 }}>
          <h3 style={{ margin: '4px 0 10px' }}>Terms, Acceptable Use, and Safety</h3>
          <TermsContent ref={termsRef} onScroll={handleTermsScroll} className="modal-scroll" />
          <div className="modal-actions">
            <button className="btn btn-ghost" onClick={() => { setShowTerms(false); }}>Close</button>
            <button
              className="btn btn-primary"
              disabled={!termsScrolled}
              onClick={() => {
                setAgree(true);
                try { localStorage.setItem(TERMS_STORAGE_KEY, 'true'); } catch { /* ignore */ }
                setShowTerms(false);
              }}
              title={!termsScrolled ? 'Scroll to the end to enable' : 'Agree to Terms'}
            >
              {termsScrolled ? 'I Agree' : 'Read to the end to Agree'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default SetupUsernamePage;
